import React, { useState, useRef, useEffect, useCallback } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAppStore } from '@/hooks/useAppStore'
import { Topbar } from '@/components/shared/Topbar'
import { EmberField } from '@/components/shared/EmberField'
import { QUIZ_QUESTIONS } from '@/data/destinations'
import type { QuizAnswer } from '@/types'

interface Bubble { id: string; role: 'ai' | 'user'; content: string }
const ROMAN = ['I', 'II', 'III', 'IV', 'V']

// Sanctum's guide persona opens in near-silence — no scenario framing, no
// enthusiasm. This line renders alone, with generous whitespace, before the
// first question ever appears.
const SILENT_OPENING = `I will not ask where you want to go. I want to understand <em>what calls you</em>.`

function ThinkingDots() {
  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center', padding: '3px 0' }}>
      {[0, 1, 2].map((i) => (
        <motion.div key={i}
          style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--candle2)' }}
          animate={{ y: [0, -5, 0], opacity: [0.3, 1, 0.3] }}
          transition={{ duration: 1.6, repeat: Infinity, delay: i * 0.32 }}
        />
      ))}
    </div>
  )
}

export function QuizScreen() {
  const { addAnswer, setScreen } = useAppStore()
  const [curQ, setCurQ] = useState(0)
  const [bubbles, setBubbles] = useState<Bubble[]>([])
  const [thinking, setThinking] = useState(false)
  const [showOpts, setShowOpts] = useState(false)
  const [inputVal, setInputVal] = useState('')
  const [openingDone, setOpeningDone] = useState(false)
  const chatRef = useRef<HTMLDivElement>(null)

  const scrollBottom = () => chatRef.current?.scrollTo({ top: chatRef.current.scrollHeight, behavior: 'smooth' })

  const pushQ = useCallback((idx: number) => {
    if (idx >= QUIZ_QUESTIONS.length) return
    const q = QUIZ_QUESTIONS[idx]
    setThinking(true); setShowOpts(false)
    setTimeout(() => {
      setThinking(false)
      const content = q.question ? `${q.intro}<br/><br/>${q.question}` : q.intro
      setBubbles((prev) => [...prev, { id: `q-${idx}`, role: 'ai', content }])
      setShowOpts(true)
      setTimeout(scrollBottom, 80)
    }, 1400) // Sanctum's slowest pacing — 600–900ms is the floor, this is the reverent ceiling
  }, [])

  // Near-silent opening: the guide's first words stand alone, unhurried,
  // before question I ever appears.
  useEffect(() => {
    setThinking(true)
    const t1 = setTimeout(() => {
      setThinking(false)
      setBubbles([{ id: 'opening', role: 'ai', content: SILENT_OPENING }])
      setTimeout(scrollBottom, 80)
    }, 900)
    const t2 = setTimeout(() => { setOpeningDone(true); pushQ(0) }, 2600)
    return () => { clearTimeout(t1); clearTimeout(t2) }
  }, [pushQ])

  useEffect(() => { scrollBottom() }, [bubbles, thinking])

  const handleAnswer = (emoji: string, label: string) => {
    addAnswer({ emoji, label, questionIndex: curQ } as QuizAnswer)
    setShowOpts(false)
    setBubbles((prev) => [...prev, { id: `a-${curQ}`, role: 'user', content: `${emoji} ${label}` }])
    const next = curQ + 1
    if (next >= QUIZ_QUESTIONS.length) {
      setTimeout(() => {
        setThinking(true)
        setTimeout(() => {
          setThinking(false)
          setBubbles((prev) => [...prev, { id: 'done', role: 'ai', content: 'Your sacred path is clear. I have found the destinations that match what calls you.' }])
          setTimeout(() => setScreen('dna'), 1800)
        }, 1300)
      }, 500)
    } else {
      setCurQ(next)
      setTimeout(() => pushQ(next), 650)
    }
  }

  // A free-text answer is stored as the user's own words (used verbatim in the
  // Claude prompt) but maps to the FIRST option of the current question for
  // DNA-scoring purposes — Sanctum still needs a scoring key even when someone
  // writes freely instead of tapping a chip.
  const handleSend = () => {
    const v = inputVal.trim(); if (!v) return
    const firstOption = QUIZ_QUESTIONS[curQ]?.options[0]
    const scoringEmoji = firstOption?.emoji ?? '✍'
    addAnswer({ emoji: scoringEmoji, label: v, questionIndex: curQ } as QuizAnswer)
    setShowOpts(false)
    setBubbles((prev) => [...prev, { id: `a-${curQ}`, role: 'user', content: v }])
    setInputVal('')
    const next = curQ + 1
    if (next >= QUIZ_QUESTIONS.length) {
      setTimeout(() => {
        setThinking(true)
        setTimeout(() => {
          setThinking(false)
          setBubbles((prev) => [...prev, { id: 'done', role: 'ai', content: 'Your sacred path is clear. I have found the destinations that match what calls you.' }])
          setTimeout(() => setScreen('dna'), 1800)
        }, 1300)
      }, 500)
    } else {
      setCurQ(next)
      setTimeout(() => pushQ(next), 650)
    }
  }

  const opts = showOpts && curQ < QUIZ_QUESTIONS.length ? QUIZ_QUESTIONS[curQ].options : []
  const activeSegment = openingDone ? curQ : -1

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: 'var(--void)', position: 'relative' }}>
      <EmberField count={14} style={{ position: 'fixed' }} />
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Topbar center={openingDone ? `${ROMAN[curQ] || 'V'} of V` : undefined} backTo="welcome" />

        {/* Segmented progress bar — 5 segments, Sanctum's reverent measure of the path walked */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 3, padding: '0 16px', height: 4, marginTop: 10, marginBottom: 6 }}>
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} style={{ background: 'rgba(201,151,58,0.12)', position: 'relative', overflow: 'hidden' }}>
              <motion.div
                style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, var(--moss), var(--candle))', transformOrigin: 'left' }}
                initial={{ scaleX: 0 }}
                animate={{ scaleX: i < activeSegment ? 1 : i === activeSegment ? 1 : 0 }}
                transition={{ duration: 0.7, ease: 'easeOut' }}
              />
            </div>
          ))}
        </div>

        {/* Chat */}
        <div ref={chatRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 16px 8px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          <AnimatePresence initial={false}>
            {bubbles.map((b) => (
              <motion.div key={b.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: 'easeOut' }}
                style={{ display: 'flex', gap: 10, justifyContent: b.role === 'user' ? 'flex-end' : 'flex-start', alignItems: 'flex-start' }}>
                {b.role === 'ai' && (
                  <div style={{ width: 34, height: 34, background: 'var(--forest3)', border: '1px solid var(--hairline2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--serif-caps)', fontSize: 11, color: 'var(--candle2)' }}>S</div>
                )}
                {b.role === 'ai' ? (
                  <div className="s-bubble" style={{ background: 'var(--forest2)', border: '1px solid var(--hairline)', padding: '16px 18px', maxWidth: '88%', fontFamily: 'var(--serif-body)', fontSize: 15, lineHeight: 1.9, color: 'var(--ivory)' }}
                    dangerouslySetInnerHTML={{ __html: b.content }} />
                ) : (
                  <div style={{ background: 'var(--candle)', color: 'var(--forest)', fontFamily: 'var(--serif-body)', fontSize: 15, padding: '10px 15px', maxWidth: '76%', border: '1px solid var(--candle2)', boxShadow: '0 0 14px rgba(201,151,58,0.25)' }}>
                    {b.content}
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {thinking && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 34, height: 34, background: 'var(--forest3)', border: '1px solid var(--hairline2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontFamily: 'var(--serif-caps)', fontSize: 11, color: 'var(--candle2)' }}>S</div>
              <div style={{ background: 'var(--forest2)', border: '1px solid var(--hairline)', padding: '14px 16px' }}>
                <ThinkingDots />
              </div>
            </motion.div>
          )}
        </div>

        {/* Options */}
        <AnimatePresence>
          {opts.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}
              style={{ padding: '0 16px 8px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {opts.map((opt, i) => (
                <motion.button key={i} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.12, duration: 0.5 }}
                  onClick={() => handleAnswer(opt.emoji, opt.label)}
                  style={{ border: '1px solid var(--hairline)', padding: '12px', fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--ivory-dim)', background: 'var(--forest2)', cursor: 'pointer', textAlign: 'left', display: 'flex', alignItems: 'flex-start', gap: 8, lineHeight: 1.4 }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--candle)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--forest3)' }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'var(--hairline)'; (e.currentTarget as HTMLButtonElement).style.background = 'var(--forest2)' }}
                >
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 2 }}>{opt.emoji}</span>
                  <span>{opt.label}</span>
                </motion.button>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Free-text input */}
        <div style={{ padding: '12px 14px', background: 'var(--forest)', borderTop: '1px solid var(--hairline)', display: 'flex', gap: 8 }}>
          <input value={inputVal} onChange={(e) => setInputVal(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            placeholder="Type freely, or tap a choice above…"
            style={{ flex: 1, background: 'rgba(248,245,239,0.06)', border: '1px solid var(--hairline2)', padding: '9px 14px', fontFamily: 'var(--serif-body)', fontSize: 14, color: 'var(--ivory)', outline: 'none', fontStyle: 'italic' }} />
          <button onClick={handleSend} style={{ width: 38, height: 38, background: 'var(--candle)', border: 'none', cursor: 'pointer', color: 'var(--forest)', fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 0 14px rgba(201,151,58,0.35)' }}>→</button>
        </div>
      </div>
    </div>
  )
}
